// Dictionary settings (docs/project.md §7; DictSettings board): languages (immutable), the
// "Teacher" block with invitations; a teacher sees "Leave dictionary" instead.
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormatter, useLocale, useTranslations } from 'use-intl';
import { languageTitle } from '@solungo/core/lang';
import { SHARE_TOKEN_TTL_DAYS } from '@solungo/core/schema';
import { api } from '@/api/endpoints';
import { ApiRequestError } from '@/api/client';
import { queryClient, sharesQuery } from '@/state/queries';
import { refreshDictionaries, useDictionary } from '@/state/session';
import { useOnline } from '@/state/online';
import { daysLeft } from '@/lib/dates';
import { dictionaryTitle } from '@/lib/dictionaryName';
import { Mail, Person } from '@/components/Icons';
import { ErrorText, Group, Section, Sheet, SheetBar, useCloseOverlay } from '@/components/ui';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/_app/d/$dictionaryId/settings')({
  component: DictionarySettings,
});

function DictionarySettings() {
  const { dictionaryId } = Route.useParams();
  const t = useTranslations('dictSettings');
  const tc = useTranslations('common');
  const ts = useTranslations('settings');
  const locale = useLocale();
  const format = useFormatter();
  const navigate = useNavigate();
  const online = useOnline();
  const dictionary = useDictionary(dictionaryId);
  const isOwner = dictionary?.role === 'owner';
  const shares = useQuery({ ...sharesQuery(dictionaryId), enabled: !!isOwner && online });
  const close = useCloseOverlay({ to: '/d/$dictionaryId', params: { dictionaryId } });
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: sharesQuery(dictionaryId).queryKey });
  const invite = useMutation({
    mutationFn: (e: string) => api.dictionaries.invite(dictionaryId, e),
    onSuccess: (_res, invited) => {
      track('teacher_invited', { email: invited });
      setEmail('');
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(e instanceof ApiRequestError && e.code === 'validation' ? tc('errorGeneric') : t('inviteError')),
  });
  const revoke = useMutation({
    mutationFn: (shareId: string) => api.dictionaries.revoke(dictionaryId, shareId),
    onSuccess: (_res, shareId) => {
      const share = shares.data?.shares.find((s) => s.id === shareId);
      track('share_revoked', { accepted: share?.status === 'accepted', email: share?.email ?? '' });
      void invalidate();
    },
  });
  const leave = useMutation({
    mutationFn: () => api.dictionaries.leave(dictionaryId),
    onSuccess: async () => {
      track('dictionary_left');
      await refreshDictionaries();
      void navigate({ to: '/', replace: true });
    },
  });

  if (!dictionary) return null;
  const name = dictionaryTitle(dictionary, locale);
  const created = format.dateTime(new Date(dictionary.createdAt), { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Sheet onClose={close} label={t('title', { name })}>
      <SheetBar onClose={close} title={t('title', { name })} />
      <div className="flex flex-col gap-[22px] overflow-y-auto px-4 pb-8 pt-3">
        <Section label={t('languages')} hint={t('pairHint')}>
          <Group>
            <Row label={t('learning')} value={languageTitle(dictionary.targetLang, locale)} />
            <Row label={t('native')} value={languageTitle(dictionary.baseLang, locale)} />
            <Row label={t('words')} value={t('wordsSince', { count: dictionary.wordCount, date: created })} />
          </Group>
        </Section>

        {isOwner ? (
          <Section label={t('teacher')} hint={t('teacherHint')}>
            <Group>
              {(shares.data?.shares ?? [])
                .filter((s) => s.status !== 'revoked')
                .map((s) => (
                  <div key={s.id} className="flex min-h-[64px] items-center gap-3 py-2.5 pl-4 pr-3" data-testid="share-row">
                    {s.status === 'accepted' ? <Person className="flex-shrink-0 text-teacher" /> : <Mail className="flex-shrink-0 text-ink-tertiary" />}
                    <span className="flex min-w-0 flex-grow flex-col gap-0.5">
                      <span className={`truncate text-[17px] leading-[22px] ${s.status === 'accepted' ? '' : 'text-ink-secondary'}`}>{s.email}</span>
                      <span className="text-[13px] leading-[17px] text-ink-secondary">
                        {s.status === 'accepted'
                          ? format.dateTime(new Date(s.acceptedAt ?? s.createdAt), { day: 'numeric', month: 'long' })
                          : t('inviteSent', { days: daysLeft(s.createdAt, SHARE_TOKEN_TTL_DAYS) })}
                      </span>
                    </span>
                    <button type="button" disabled={!online || revoke.isPending} className="h-11 flex-shrink-0 px-1 text-[15px] font-medium text-[#C8102E] disabled:opacity-40" onClick={() => revoke.mutate(s.id)}>
                      {s.status === 'accepted' ? t('revoke') : t('cancelInvite')}
                    </button>
                  </div>
                ))}
              <form
                className="flex min-h-[56px] items-center gap-3 px-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (email.trim()) invite.mutate(email.trim());
                }}
              >
                <span className="flex w-[22px] items-center justify-center text-accent">
                  <Mail />
                </span>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('teacherEmail')} aria-label={t('teacherEmail')} data-testid="teacher-email" disabled={!online} className="min-w-0 flex-grow bg-transparent text-[17px] outline-none placeholder:text-ink-tertiary" />
                <button type="submit" disabled={!online || invite.isPending || !email.trim()} className="h-11 flex-shrink-0 px-1 text-[15px] font-medium text-accent disabled:opacity-40" data-testid="invite">
                  {t('invite')}
                </button>
              </form>
            </Group>
            <ErrorText>{error}</ErrorText>
          </Section>
        ) : (
          <Section label={t('leaveTitle')} hint={t('leaveHint')}>
            <Group>
              <button
                type="button"
                disabled={!online || leave.isPending}
                className="flex min-h-[50px] w-full items-center px-4 text-left text-[17px] text-[#C8102E] disabled:opacity-40"
                onClick={() => {
                  if (window.confirm(ts('leaveConfirm', { name }))) leave.mutate();
                }}
              >
                {t('leaveDictionary')}
              </button>
            </Group>
          </Section>
        )}
      </div>
    </Sheet>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-h-[50px] items-center gap-3 px-4">
      <span className="flex-grow text-[17px]">{label}</span>
      <span className="text-right text-[17px] text-ink-secondary">{value}</span>
    </div>
  );
}
